import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";
import { router } from "../router";
import { DialogRouter } from "../boot/dialogs";
import { forEach, cloneDeep } from "lodash";

export const SERVICE_STATUS_FIELDS = {
  type: null,
  loaded: {},
  delivered: {},
  loadingStarted: null,
  loadingCompleted: null,
  departed: null,
  arrived: null,
  unloaded: null,
  undeliveredUnloaded: false,
  signature: null,
  signerName: null,
  noSignature: false,
  invoice: null,
  proof: [],
};
deepFreeze(SERVICE_STATUS_FIELDS);

function goToLoadingTruck() {
  let item = this._parent.item;
  if (item) {
    router.push({
      name: "loading-truck",
      params: {
        projectID: item.projectID,
        saleID: item.id,
        deliveryID: this._parent.id,
      },
    });
  }
}

function goToServiceLoading() {
  // For service events, open the service work order loading dialog
  const workOrderID = this._parent.data.item;
  if (workOrderID) {
    DialogRouter.push({
      name: "service-work-order-loading",
      params: {
        workOrderID: workOrderID,
      },
    });
  }
}

function serviceActions() {
  // For service events, open the service work order edit dialog
  const workOrderID = this._parent.data.item;
  if (workOrderID) {
    DialogRouter.push({
      name: "service-work-order-edit",
      params: {
        workOrderID: workOrderID,
      },
    });
  } else {
    // Fallback to event edit if no work order linked
    DialogRouter.push({
      name: "event-edit",
      params: {
        eventID: this._parent.id,
      },
    });
  }
}

function unloadTruck() {
  DialogRouter.push({
    name: "service-unload",
    params: {
      eventID: this._parent.id,
    },
  });
}
function edit() {
  DialogRouter.push({
    name: "event-edit",
    params: {
      eventID: this._parent.id,
    },
  });
}

export const SERVICE_STATUS_TYPES = {
  // Status for rescheduled services needing unload
  unload: {
    order: 0,
    label: "Unload",
    icon: "unloading",
    color: "negative",
    action: unloadTruck,
  },
  reschedule: {
    order: 1,
    label: "Reschedule",
    icon: "reschedule",
    color: "negative",
    action: edit,
  },
  // Labor-only status: just scheduled, no parts
  scheduled: {
    order: 2,
    label: "Scheduled",
    icon: "calendar",
    color: "positive",
    action: serviceActions,
  },
  // Parts workflow statuses
  ready: {
    order: 3,
    label: "Ready to load",
    icon: "loading_ready",
    color: "warning",
    action: goToServiceLoading,
  },
  loading: {
    order: 4,
    label: "Loading",
    icon: "loading",
    color: "warning",
    time: "loadingStarted",
    action: goToServiceLoading,
  },
  loaded: {
    order: 5,
    label: "Ready",
    icon: "loaded",
    color: "positive",
    time: "loadingCompleted",
    action: serviceActions,
  },
  // Common statuses for both workflows
  transit: {
    order: 6,
    label: "In transit",
    icon: "delivering",
    color: "warning",
    time: "departed",
    action: serviceActions,
  },
  onsite: {
    order: 7,
    label: "On site",
    icon: "item_location",
    color: "warning",
    time: "arrived",
    action: serviceActions,
  },
  // Legacy unloading status (for parts). Shares `onsite`'s order and label on
  // purpose: it is the same operational state under an older name, so the two
  // must sort together in the status columns.
  unloading: {
    order: 7,
    label: "On site",
    icon: "unloading",
    color: "warning",
    time: "arrived",
    action: serviceActions,
  },
  deliveredPartial: {
    order: 8,
    label: "Partially complete",
    icon: "warning",
    color: "warning",
    time: "unloaded",
    action: serviceActions,
  },
  delivered: {
    order: 9,
    label: "Completed",
    icon: "done",
    color: "positive",
    time: "unloaded",
    action: serviceActions,
  },
  completed: {
    order: 9,
    label: "Completed",
    icon: "done",
    color: "positive",
    action: serviceActions,
  },
};
deepFreeze(SERVICE_STATUS_TYPES);

export const SERVICE_COMPLETION_TYPES = {
  complete: {
    order: 3,
    label: "Complete",
    icon: "done",
    color: "positive",
  },
  partial: {
    order: 2,
    label: "Partial",
    icon: "warning",
    color: "warning",
  },
  reschedule: {
    order: 1,
    label: "Reschedule",
    icon: "reschedule",
    color: "negative",
  },
};
deepFreeze(SERVICE_COMPLETION_TYPES);

export default class ServiceStatus extends DataStructure {
  constructor(parent, data = {}) {
    const needsSanitize = Object.keys(SERVICE_STATUS_FIELDS).some(
      (key) => !(key in data)
    );
    if (Object.isFrozen(data) || needsSanitize) {
      data = sanitize(
        Object.isFrozen(data) ? cloneDeep(data) : data,
        SERVICE_STATUS_FIELDS
      );
    }
    data.loadingStarted = timestampToDate(data.loadingStarted);
    data.loadingCompleted = timestampToDate(data.loadingCompleted);
    data.departed = timestampToDate(data.departed);
    data.arrived = timestampToDate(data.arrived);
    data.unloaded = timestampToDate(data.unloaded);

    super(data);

    this._parent = parent;
    // Always update type based on current conditions
    this.data.type = this.get();
  }

  get() {
    // Reschedule short-circuits: needs unload or reschedule actions first
    if (this._parent.data.rescheduleReason) {
      return this.quantityToUnload ? "unload" : "reschedule";
    }

    // Check linked work order status — if completed, reflect that immediately
    const workOrderID = this._parent.data.item;
    const workOrder = workOrderID
      ? store.state.main.serviceWorkOrders?.[workOrderID]
      : null;
    if (workOrder?.data?.status === "completed") {
      return "completed";
    }

    // Check if this is a labor-only service (no parts to load)
    const hasParts = this.hasPartsToLoad;

    if (hasParts) {
      // Parts workflow: ready -> loading -> loaded -> transit -> onsite -> completed
      // Check work order loading status first, then fall back to event status data
      const loadingStarted =
        workOrder?.data?.loadingStarted || this.data.loadingStarted;
      const loadingCompleted =
        workOrder?.data?.loadingCompleted || this.data.loadingCompleted;

      if (!loadingStarted) {
        return "ready";
      } else if (!loadingCompleted) {
        return "loading";
      } else if (!this.data.departed) {
        return "loaded";
      } else if (!this.data.arrived) {
        return "transit";
      } else if (!this.data.unloaded) {
        return "unloading";
      } else if (this.quantityToUnload > 0 || this.data.undeliveredUnloaded) {
        // Only partial while something is still ON the truck (or was unloaded
        // undelivered). `percentDelivered` compares against the parent's full
        // quantity, so a stale/short delivered map leaves it below 100 forever
        // and the status sticks at "deliveredPartial" with nothing left to
        // unload. Mirrors DeliveryStatus.get().
        return "deliveredPartial";
      } else {
        return "delivered";
      }
    } else {
      // Labor-only workflow: scheduled -> onsite -> completed.
      //
      // Deliberately NO transit state, which is why both middle branches
      // return "onsite". With no parts to load there is nothing to track
      // between departing and arriving: the tech is either not started, at the
      // job, or done. Reporting "In Transit" here would give schedulers a
      // status they can neither act on nor advance, since the departed/arrived
      // timestamps are only stamped by the parts-loading flow.
      if (!this.data.departed) {
        return "scheduled";
      } else if (!this.data.arrived) {
        return "onsite";
      } else if (!this.data.unloaded) {
        return "onsite";
      } else {
        return "completed";
      }
    }
  }

  get hasPartsToLoad() {
    // Check if the linked work order has parts to load
    const workOrderID = this._parent.data.item;
    if (workOrderID) {
      const workOrder = store.state.main.serviceWorkOrders?.[workOrderID];
      if (workOrder) {
        // Has parts only if partsToLoad array explicitly has items
        return (
          workOrder.data.partsToLoad && workOrder.data.partsToLoad.length > 0
        );
      }
    }
    // For service events, default to no parts (labor-only) unless explicitly set
    // Check if event has items array with actual items
    const items = this._parent.data.items;
    return items && Array.isArray(items) && items.length > 0;
  }

  // Formatting

  get loadedCount() {
    return Object.values(this.data.loaded).reduce(
      (total, count) => total + count,
      0
    );
  }

  get deliveredCount() {
    return Object.values(this.data.delivered).reduce(
      (total, count) => total + count,
      0
    );
  }

  get percentLoaded() {
    const numerator = this.loadedCount;
    const denominator = this._parent.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get percentDelivered() {
    const numerator = this.deliveredCount;
    const denominator = this._parent.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get itemsLoaded() {
    let items = {};
    forEach(this.data.loaded, (quantity, key) => {
      let id = key.split("-")[0];
      if (id in items) {
        items[id] += quantity;
      } else {
        items[id] = quantity;
      }
    });
    return items;
  }

  get itemsToUnload() {
    let items = {};
    forEach(this.data.loaded, (quantity, key) => {
      let id = key.split("-")[0];
      if (id in items) {
        items[id] += quantity;
      } else {
        items[id] = quantity;
      }
    });
    forEach(this.data.delivered, (quantity, id) => {
      items[id] -= quantity;
      if (!items[id]) {
        delete items[id];
      }
    });
    return items;
  }

  get quantityToUnload() {
    return Object.values(this.itemsToUnload).reduce(
      (total, count) => total + count,
      0
    );
  }

  get proof() {
    return this.data.proof.map((d) => new DocumentRef(d));
  }

  get invoice() {
    return this.data.invoice
      ? store.state.main.invoices[this.data.invoice]
      : null;
  }

  get time() {
    const type = SERVICE_STATUS_TYPES[this.currentType];
    return type?.time ? this.data[type.time] : null;
  }

  get currentType() {
    // Always recalculate type dynamically to reflect work order changes
    return this.get();
  }

  get type() {
    const type = SERVICE_STATUS_TYPES[this.currentType];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get action() {
    let type = this.type;
    return type && type.action ? type.action.bind(this) : null;
  }
}
